<template>
  <div class="d-flex flex-column" style="height: 100%">
    <!-- Chat Header -->
    <ChatHeader
      :chat="chat"
      @toggleGroupInfo="$emit('toggleGroupInfo')"
      @back="$emit('back')"
    />

    <!-- Messages Container -->
    <div
      ref="messagesContainer"
      class="flex-grow-1 overflow-y-auto"
      @scroll="handleScroll"
      style="background: #e5ddd5; padding: 8px 0 4px"
    >
      <!-- Load More Button -->
      <div v-if="hasMoreMessages" class="text-center py-2 px-4">
        <v-btn
          small
          rounded
          depressed
          color="white"
          @click="loadMoreMessages"
          :loading="loadingMore"
          style="box-shadow: 0 1px 2px rgba(0,0,0,0.2)"
        >
          <v-icon left small>mdi-chevron-up</v-icon>
          Load older messages
        </v-btn>
      </div>

      <!-- Loading indicator -->
      <v-progress-linear
        v-if="loadingMessages && messages.length === 0"
        indeterminate
        color="primary"
        class="mb-4"
      ></v-progress-linear>

      <!-- Messages grouped by date -->
      <div v-for="(group, date) in groupedMessages" :key="date">
        <!-- Date separator -->
        <div class="text-center my-3 px-4">
          <v-chip
            small
            color="rgba(255,255,255,0.85)"
            style="font-size: 11px; font-weight: 600; letter-spacing: 0.3px; box-shadow: 0 1px 2px rgba(0,0,0,0.15)"
          >
            {{ date }}
          </v-chip>
        </div>

        <!-- Messages for this date -->
        <MessageBubble
          v-for="message in group"
          :key="message.id"
          :message="message"
          :is-own="message.sender_id === user.id"
          :is-group="!!chat.is_group"
          @imageClick="handleImageClick"
          @retry="handleRetry"
        />
      </div>

      <!-- Typing Indicator -->
      <TypingIndicator :users="typingUsers" :chat="chat" />

      <!-- Empty state -->
      <div
        v-if="messages.length === 0 && !loadingMessages"
        class="d-flex flex-column align-center justify-center"
        style="min-height: 200px; padding-top: 60px"
      >
        <v-icon size="72" color="rgba(0,0,0,0.2)">mdi-message-text-outline</v-icon>
        <p class="text-subtitle-1 mt-4" style="color: rgba(0,0,0,0.4)">No messages yet</p>
        <p class="text-caption" style="color: rgba(0,0,0,0.3)">Say hello to start the conversation!</p>
      </div>
    </div>

    <!-- Message Input -->
    <MessageInput :chat="chat" />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import ChatHeader from './ChatHeader.vue';
import MessageBubble from './MessageBubble.vue';
import MessageInput from './MessageInput.vue';
import TypingIndicator from './TypingIndicator.vue';

export default {
  name: 'MessageThread',

  components: {
    ChatHeader,
    MessageBubble,
    MessageInput,
    TypingIndicator
  },

  props: {
    chat: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      page: 1,
      hasMoreMessages: true,
      loadingMore: false,
      observer: null,
      markSeenTimeout: null
    };
  },

  computed: {
    ...mapState('chat', ['loadingMessages']),
    ...mapGetters('chat', ['getChatMessages', 'getTypingUsers']),

    messages() {
      return this.getChatMessages(this.chat.id);
    },

    typingUsers() {
      return this.getTypingUsers(this.chat.id);
    },

    groupedMessages() {
      const groups = {};

      this.messages.forEach((message) => {
        const date = new Date(message.created_at);
        const dateKey = this.formatDateKey(date);

        if (!groups[dateKey]) {
          groups[dateKey] = [];
        }
        groups[dateKey].push(message);
      });

      return groups;
    }
  },

  watch: {
    'messages.length'() {
      this.scrollToBottom();
      this.setupIntersectionObserver();
    },

    chat(newChat, oldChat) {
      if (newChat && newChat.id !== oldChat?.id) {
        this.page = 1;
        this.hasMoreMessages = true;
        this.scrollToBottom(false);
      }
    }
  },

  mounted() {
    this.scrollToBottom(false);
    this.setupIntersectionObserver();
  },

  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    clearTimeout(this.markSeenTimeout);
  },

  methods: {
    formatDateKey(date) {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

      if (messageDate.getTime() === today.getTime()) {
        return 'Today';
      } else if (messageDate.getTime() === yesterday.getTime()) {
        return 'Yesterday';
      } else {
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
        });
      }
    },

    scrollToBottom(smooth = true) {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const container = this.$refs.messagesContainer;
          if (container) {
            container.scrollTo({
              top: container.scrollHeight,
              behavior: smooth ? 'smooth' : 'auto'
            });
          }
        });
      });
    },

    async handleScroll(event) {
      const { scrollTop } = event.target;

      // If scrolled to top, load more messages
      if (scrollTop === 0 && this.hasMoreMessages && !this.loadingMore) {
        await this.loadMoreMessages();
      }
    },

    async loadMoreMessages() {
      if (this.loadingMore || !this.hasMoreMessages) return;

      this.loadingMore = true;
      const previousScrollHeight = this.$refs.messagesContainer.scrollHeight;

      try {
        this.page++;
        const result = await this.$store.dispatch('chat/fetchMessages', {
          chatId: this.chat.id,
          page: this.page,
          rowsPerPage: 50
        });

        this.hasMoreMessages = result.hasMore;

        // Preserve scroll position
        this.$nextTick(() => {
          const container = this.$refs.messagesContainer;
          if (container) {
            container.scrollTop = container.scrollHeight - previousScrollHeight;
          }
        });
      } catch (error) {
        console.error('Failed to load more messages:', error);
        this.$toast.error('Failed to load more messages');
        this.page--;
      } finally {
        this.loadingMore = false;
      }
    },

    setupIntersectionObserver() {
      if (this.observer) {
        this.observer.disconnect();
      }

      const options = {
        root: this.$refs.messagesContainer,
        rootMargin: '0px',
        threshold: 0.5
      };

      this.observer = new IntersectionObserver(this.handleIntersection, options);

      // Observe all message bubbles
      this.$nextTick(() => {
        const messageBubbles = this.$refs.messagesContainer?.querySelectorAll('.message-bubble');
        if (messageBubbles) {
          messageBubbles.forEach((bubble) => {
            this.observer.observe(bubble);
          });
        }
      });
    },

    handleIntersection(entries) {
      let hasUnseenMessages = false;

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const messageId = parseInt(entry.target.dataset.messageId);
          const message = this.messages.find((m) => m.id === messageId);

          if (message && !message.seen && message.sender_id !== this.user.id) {
            hasUnseenMessages = true;
          }
        }
      });

      if (hasUnseenMessages) {
        // Debounce the API call
        clearTimeout(this.markSeenTimeout);
        this.markSeenTimeout = setTimeout(() => {
          this.markVisibleMessagesAsSeen();
        }, 1000);
      }
    },

    markVisibleMessagesAsSeen() {
      this.$store.dispatch('chat/markChatAsSeen', this.chat.id);
    },

    handleImageClick(imagePath) {
      // Collect all images from messages
      const images = this.messages
        .filter((m) => m.file_path && this.isImage(m.file_path))
        .map((m) => m.file_path);

      const index = images.indexOf(imagePath);
      this.$parent.openImagePreview(images, index);
    },

    handleRetry(message) {
      this.$store.dispatch('chat/retryMessage', {
        chatId: this.chat.id,
        message
      });
    },

    isImage(filePath) {
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
      const extension = filePath.split('.').pop().toLowerCase();
      return imageExtensions.includes(extension);
    }
  }
};
</script>

<style scoped>
/* Custom scrollbar */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.5);
}
</style>
